import { Component } from '@nicolas-riciotti/light2';

export default class BlockWidget extends Component {
  attached() {
    // The template holds the pasted document base64-encoded, so WP content
    // filters (wptexturize, wpautop) can't corrupt it. Decode as UTF-8.
    const template = this.querySelector('template[data-widget-html]');
    const encoded = template ? template.content.textContent.trim() : '';
    if (!encoded) return;

    const html = new TextDecoder().decode(
      Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0))
    );
    if (!html.trim()) return;

    const title = this.getAttribute('data-title') || 'Embedded widget';

    let iframe = null;
    let built = false;

    // Appended to the pasted document so the sandboxed iframe reports its
    // content height. srcdoc + sandbox pages have a null origin, so the
    // message targets '*' and is matched back to this block by window
    // identity in handleMessage.
    //
    // The height is the bottom edge of <body> (plus its bottom margin), NOT
    // documentElement.scrollHeight: inside an iframe scrollHeight can never be
    // less than the iframe's own height, so once the frame had been sized to an
    // early, taller layout it could grow but never shrink. Widgets that declare
    // the site's web fonts render in a fallback face first (the sandboxed frame
    // can't reuse the page's font cache), and when the real font swaps in and
    // the content gets shorter the surplus stayed as blank space under the
    // widget, which people reported as a bug on mobile where the swap moves
    // the most lines. Body is observed as well as <html>, and fonts.ready
    // triggers one more measurement, so the frame follows the content down.
    // Body's own box can miss absolutely positioned children (a chart
    // container, say), so the bottom edge of each direct child is taken into
    // account too; scrollHeight is only the last resort for an empty layout.
    const RESIZE_SNIPPET = '<script>(function(){var d=document.documentElement,b=document.body,p=function(){var y=window.scrollY||0,r=b.getBoundingClientRect(),m=parseFloat(getComputedStyle(b).marginBottom)||0,h=r.bottom+m;for(var i=0;i<b.children.length;i++){var c=b.children[i].getBoundingClientRect();if(c.bottom>h)h=c.bottom}h=Math.ceil(h+y);if(!(h>0))h=d.scrollHeight;parent.postMessage({source:"block-widget",type:"height",height:h},"*")};new ResizeObserver(p).observe(d);new ResizeObserver(p).observe(b);addEventListener("load",p);if(document.fonts&&document.fonts.ready)document.fonts.ready.then(p)})()<\/script>';

    const handleMessage = (event) => {
      if (!iframe || event.source !== iframe.contentWindow) return;

      const data = event.data;
      if (!data || data.source !== 'block-widget' || data.type !== 'height') return;

      // Clamp so a buggy or hostile widget can't blow the page layout up.
      const height = Math.min(Math.ceil(data.height), 10000);
      iframe.style.height = `${height}px`;
    };

    const build = () => {
      if (built) return;
      built = true;

      this.style.display = 'block';
      this.style.width = '100%';

      iframe = document.createElement('iframe');
      // SECURITY: 'allow-scripts' WITHOUT 'allow-same-origin' is the entire
      // security model here — pasted code runs in a null origin with no access
      // to the site's cookies, session, or DOM. Never add 'allow-same-origin':
      // combined with srcdoc it makes pasted code same-origin with the site,
      // i.e. full XSS.
      //
      // 'allow-popups' lets a widget's target="_blank" links open (a CTA into
      // the playground, say); popups still need a real click. Without
      // 'allow-popups-to-escape-sandbox' the new tab would inherit this
      // sandbox and the page it opens would not work. Same-tab navigation
      // stays blocked.
      iframe.setAttribute('sandbox', 'allow-scripts allow-popups allow-popups-to-escape-sandbox');
      iframe.setAttribute('title', title);
      iframe.setAttribute('scrolling', 'no');
      iframe.setAttribute('referrerpolicy', 'no-referrer');
      iframe.style.cssText = 'display:block;width:100%;height:0;border:0;overflow:hidden;';
      iframe.srcdoc = html + RESIZE_SNIPPET;

      this.appendChild(iframe);
    };

    window.addEventListener('message', handleMessage);

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !built) {
        observer.disconnect();
        build();
      }
    }, {
      threshold: 0.01,
      rootMargin: '0px 0px 300px 0px',
    });

    observer.observe(this);

    this._blockWidgetCleanup = () => {
      observer.disconnect();
      window.removeEventListener('message', handleMessage);
    };
  }

  detached() {
    if (this._blockWidgetCleanup) {
      this._blockWidgetCleanup();
      this._blockWidgetCleanup = null;
    }
  }
}

customElements.define('block-widget', BlockWidget);
